/**
 * Runtime contract for the operational archetype's stages and their ownership of
 * controls and regions.
 *
 * Split out of `FmOperationalWorkspaceRoot.validation.ts` so neither file
 * crosses the 500-line gate.
 */
import {
  exactOperationalKeys as exact,
  failOperationalDescriptor as fail,
  operationalArray as array,
  operationalObject as object,
  operationalOneOf as oneOf,
  operationalText as text,
  optionalOperationalText as optionalText,
} from './FmOperationalWorkspaceValidation.shared';

function validateAnswerFigure(value: unknown, path: string): void {
  const figure = object(value, path);
  exact(figure, new Set(['id', 'label', 'value', 'unit', 'caption']), path);
  text(figure.id, `${path}.id`);
  text(figure.label, `${path}.label`);
  if (figure.value !== null) text(figure.value, `${path}.value`);
  optionalText(figure.unit, `${path}.unit`);
  optionalText(figure.caption, `${path}.caption`);
  // An unserved figure must say why; an empty slot never reads as an answer.
  if (figure.value === null && figure.caption === undefined) {
    fail(`${path}.caption`, 'must name why the figure is unavailable');
  }
}

export function validateStages(value: unknown, activeStageId: unknown): void {
  const stages = array(value, 'descriptor.stages');
  if (!stages.length) fail('descriptor.stages', 'must contain at least one stage');
  const stageIds = new Set<string>();
  stages.forEach((item, index) => {
    const path = `descriptor.stages[${index}]`;
    const stage = object(item, path);
    exact(
      stage,
      new Set([
        'id',
        'label',
        'description',
        'ownerQuestion',
        'literalAnswer',
        'answerFigure',
        'dominantRegionId',
        'transition',
        'transitionActionLabel',
        'mobileRegionOrder',
        'conversionRole',
        'status',
        'disabledReason',
        'regionIds',
        'controlIds',
      ]),
      path,
    );
    stageIds.add(text(stage.id, `${path}.id`));
    text(stage.label, `${path}.label`);
    text(stage.ownerQuestion, `${path}.ownerQuestion`);
    text(stage.literalAnswer, `${path}.literalAnswer`);
    if (stage.answerFigure !== undefined) validateAnswerFigure(stage.answerFigure, `${path}.answerFigure`);
    text(stage.dominantRegionId, `${path}.dominantRegionId`);
    text(stage.transition, `${path}.transition`);
    optionalText(stage.transitionActionLabel, `${path}.transitionActionLabel`);
    oneOf(stage.conversionRole, ['orient', 'evaluate', 'commit'], `${path}.conversionRole`);
    oneOf(stage.status, ['available', 'current', 'complete', 'blocked'], `${path}.status`);
    optionalText(stage.description, `${path}.description`);
    optionalText(stage.disabledReason, `${path}.disabledReason`);
    for (const ownedKey of ['regionIds', 'controlIds'] as const) {
      const ownedIds = array(stage[ownedKey], `${path}.${ownedKey}`).map((ownedId, ownedIndex) =>
        text(ownedId, `${path}.${ownedKey}[${ownedIndex}]`),
      );
      if (new Set(ownedIds).size !== ownedIds.length)
        fail(`${path}.${ownedKey}`, 'must use unique ids');
    }
    const regionIds = array(stage.regionIds, `${path}.regionIds`).map(String);
    if (!regionIds.includes(String(stage.dominantRegionId))) {
      fail(`${path}.dominantRegionId`, 'must reference a region owned by the stage');
    }
    const mobileRegionOrder = array(stage.mobileRegionOrder, `${path}.mobileRegionOrder`).map(
      (ownedId, ownedIndex) => text(ownedId, `${path}.mobileRegionOrder[${ownedIndex}]`),
    );
    if (
      mobileRegionOrder.length !== regionIds.length ||
      mobileRegionOrder.some((regionId) => !regionIds.includes(regionId))
    ) {
      fail(`${path}.mobileRegionOrder`, 'must order every region owned by the stage exactly once');
    }
    if (new Set(mobileRegionOrder).size !== mobileRegionOrder.length) {
      fail(`${path}.mobileRegionOrder`, 'must use unique ids');
    }
  });
  if (!stageIds.has(String(activeStageId)))
    fail('descriptor.activeStageId', 'must reference a declared stage');
  if (stageIds.size !== stages.length) fail('descriptor.stages', 'must use unique ids');
  const currentStages = stages.filter(
    (item) => object(item, 'descriptor stage').status === 'current',
  );
  if (
    currentStages.length !== 1 ||
    object(currentStages[0], 'descriptor current stage').id !== activeStageId
  ) {
    fail('descriptor.stages', 'must mark exactly the active stage as current');
  }
}

export function validateStageOwnership(
  stagesValue: unknown,
  controls: Record<string, unknown>[],
  regionsValue: unknown,
): void {
  const stages = array(stagesValue, 'descriptor.stages').map((item, index) =>
    object(item, `descriptor.stages[${index}]`),
  );
  const controlIds = new Set(controls.map((control) => String(control.id)));
  const regionIds = new Set(
    array(regionsValue, 'descriptor.regions').map((item, index) =>
      text(object(item, `descriptor.regions[${index}]`).id, `descriptor.regions[${index}].id`),
    ),
  );
  const claimedControls = new Set<string>();
  const claimedRegions = new Set<string>();
  stages.forEach((stage, stageIndex) => {
    for (const [key, declaredIds, claimedIds] of [
      ['regionIds', regionIds, claimedRegions],
      ['controlIds', controlIds, claimedControls],
    ] as const) {
      array(stage[key], `descriptor.stages[${stageIndex}].${key}`).forEach((idValue, idIndex) => {
        const path = `descriptor.stages[${stageIndex}].${key}[${idIndex}]`;
        const id = text(idValue, path);
        if (!declaredIds.has(id)) fail(path, 'must reference a declared descriptor id');
        // Controls stay unique. The same region id may appear on more than one
        // stage (Decision + Plan both listing property-map).
        if (key === 'controlIds' && claimedIds.has(id)) {
          fail(path, 'must be owned by exactly one stage');
        }
        claimedIds.add(id);
      });
    }
  });
  for (const id of regionIds)
    if (!claimedRegions.has(id)) fail('descriptor.stages', `must assign region ${id}`);
  for (const id of controlIds)
    if (!claimedControls.has(id)) fail('descriptor.stages', `must assign control ${id}`);
}
